import { Clipboard } from '@angular/cdk/clipboard';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  OnDestroy,
  signal,
  type Signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  MultifactorService,
  UserPublicIpService,
} from '@principle-theorem/ng-auth';
import {
  isIpExempt,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  canSave$,
  DialogPresets,
  formControlChanges$,
  IBreadcrumb,
  isValid$,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import { Organisation, Staffer } from '@principle-theorem/principle-core';
import {
  IPractice,
  IPracticeIpWhitelist,
  IPracticeRestrictions,
  IPracticeUserRestriction,
  IStaffer,
  IUser,
  IUserRestrictionDay,
  MultifactorOverride,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  Firestore,
  formatTimeStringDisplay,
  INamedDocument,
  isSameRef,
  shareReplayCold,
  DayOfWeek,
  DAYS_OF_WEEK,
  hasSameDays,
  snapshot,
  WEEK_DAYS,
  WEEKEND_DAYS,
  WithRef,
} from '@principle-theorem/shared';
import { isIP } from 'is-ip';
import { first, sortBy, startCase } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  skip,
  switchMap,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import {
  EditUserRestrictionDialogComponent,
  IEditUserRestrictionRequest,
} from './edit-user-restriction-dialog/edit-user-restriction-dialog.component';
import { UserRestrictionIpOverrideFormGroup } from './user-restriction-ip-override-form';
import { UserRestrictionScheduleFormGroup } from './user-restriction-schedule-form';

interface IUserRestrictionForm {
  isEnabled: boolean;
  staff: WithRef<IStaffer>[];
}

interface IUserRestrictionRow {
  restriction: IPracticeUserRestriction;
  isIpExempt: boolean;
}

@Component({
  selector: 'pr-security',
  templateUrl: './security.component.html',
  styleUrl: './security.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SecurityComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  breadcrumbs: IBreadcrumb[] = [
    { label: 'Settings', path: '../../' },
    { label: 'Security' },
  ];
  isIpValidatorFn = isIP;
  separatorKeysCodes = [ENTER, COMMA];
  saveDisabled$: Observable<boolean>;
  canAdd$: Observable<boolean>;
  userIp$: Observable<string | undefined>;
  staffOptions$: Observable<WithRef<IStaffer>[]>;
  practiceStaff$: Observable<WithRef<IStaffer>[]>;
  userRestrictions = signal<IPracticeUserRestriction[]>([]);
  ipExemptUsers = signal<INamedDocument<IUser>[]>([]);
  userRestrictionRows: Signal<IUserRestrictionRow[]>;
  selectedPractice = signal<WithRef<IPractice> | undefined>(undefined);
  staffSelectorCtrl = new TypedFormControl<WithRef<IStaffer>[]>([]);
  ipExemptUsersCtrl = new TypedFormControl<INamedDocument<IUser>[]>([]);
  scheduleGroup = new UserRestrictionScheduleFormGroup();
  ipOverrideGroup = new UserRestrictionIpOverrideFormGroup();
  ipWhitelistForm = new TypedFormGroup<IPracticeIpWhitelist>({
    isEnabled: new TypedFormControl<boolean>(undefined),
    whitelist: new TypedFormControl<string[]>([]),
  });
  userRestrictionsForm = new TypedFormGroup<IUserRestrictionForm>({
    isEnabled: new TypedFormControl<boolean>(undefined),
    staff: this.staffSelectorCtrl,
  });

  isMultifactorEnabled$: Observable<boolean>;

  constructor(
    public organisation: OrganisationService,
    private _ipService: UserPublicIpService,
    private _snackBar: MatSnackBar,
    private _basicDialog: BasicDialogService,
    private _clipboard: Clipboard,
    private _multifactorAdmin: MultifactorService
  ) {
    this.userIp$ = this._ipService.getIpAddress$();

    this.isMultifactorEnabled$ = this.organisation.organisation$.pipe(
      map((org) => org?.settings?.multifactor?.enabled ?? false),
      shareReplayCold()
    );

    this.organisation.practice$
      .pipe(take(1), filterUndefined(), takeUntil(this._onDestroy$))
      .subscribe((practice) => this.selectedPractice.set(practice));

    const practice$ = toObservable(this.selectedPractice).pipe(
      filterUndefined(),
      distinctUntilChanged(isSameRef),
      withLatestFrom(this.organisation.practices$),
      map(([selectedPractice, practices]) =>
        practices.find((practice) => isSameRef(practice, selectedPractice))
      ),
      filterUndefined()
    );

    this.practiceStaff$ = practice$.pipe(
      switchMap((practice) => Staffer.byPractice$(practice)),
      shareReplayCold()
    );

    this.staffOptions$ = combineLatest([
      this.practiceStaff$,
      toObservable(this.userRestrictions),
    ]).pipe(
      map(([staff, restrictions]) =>
        staff.filter(
          (staffer) =>
            !restrictions.some((restriction) =>
              isSameRef(restriction.user, staffer.user)
            )
        )
      ),
      shareReplayCold()
    );

    practice$
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(({ restrictions }) => this.patchForm(restrictions));

    practice$
      .pipe(skip(1), takeUntil(this._onDestroy$))
      .subscribe(({ name }) =>
        this._snackBar.open(`Practice changed to ${name}`)
      );

    formControlChanges$(this.ipExemptUsersCtrl)
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((exemptUsers) => this.ipExemptUsers.set(exemptUsers ?? []));

    this.userRestrictionRows = computed(() =>
      this.userRestrictions().map((restriction) => ({
        restriction,
        isIpExempt: isIpExempt(
          { ipExemptUsers: this.ipExemptUsers() },
          restriction.user
        ),
      }))
    );

    this.saveDisabled$ = combineLatest([
      canSave$(this.ipWhitelistForm),
      canSave$(this.userRestrictionsForm),
      canSave$(this.ipExemptUsersCtrl),
    ]).pipe(map((canSaveStates) => !canSaveStates.some((canSave) => canSave)));

    this.canAdd$ = combineLatest([
      isValid$(this.scheduleGroup),
      formControlChanges$(this.staffSelectorCtrl),
    ]).pipe(
      map(([scheduleValid, staff]) => scheduleValid && !!staff?.length),
      shareReplayCold()
    );
  }

  copyIpToClipboard(ip: string): void {
    this._snackBar.open('Copied to clipboard');
    this._clipboard.copy(ip);
  }

  addUserRestriction(): void {
    const { staff, isEnabled } = this.userRestrictionsForm.getRawValue();
    const schedule = this.scheduleGroup.getSchedule();
    const ipWhitelist = this.ipOverrideGroup.getIpWhitelist();
    const addedRestrictions = staff.map(
      ({ user }): IPracticeUserRestriction => ({
        user,
        schedule,
        ipWhitelist,
      })
    );

    this.userRestrictions.set(
      this.userRestrictions().concat(addedRestrictions)
    );
    this.userRestrictionsForm.reset();
    this.userRestrictionsForm.patchValue({ isEnabled });
    this.userRestrictionsForm.markAsDirty();
    this.scheduleGroup.resetToDefault();
    this.ipOverrideGroup.setIpWhitelist();
  }

  async editRestriction(restriction: IPracticeUserRestriction): Promise<void> {
    const data: IEditUserRestrictionRequest = { restriction };
    const updated = await snapshot(
      this._basicDialog
        .open<
          EditUserRestrictionDialogComponent,
          IEditUserRestrictionRequest,
          IPracticeUserRestriction
        >(
          EditUserRestrictionDialogComponent,
          DialogPresets.large({ data, maxHeight: '80vh' })
        )
        .afterClosed()
    );
    if (!updated) {
      return;
    }

    this.userRestrictions.update((restrictions) =>
      restrictions.map((existing) =>
        isSameRef(existing.user, restriction.user) ? updated : existing
      )
    );
    this.userRestrictionsForm.markAsDirty();
    this.userRestrictionsForm.updateValueAndValidity();
  }

  deleteRestriction(restriction: IPracticeUserRestriction): void {
    this.userRestrictions.set(
      this.userRestrictions().filter(
        (existing) => !isSameRef(existing.user, restriction.user)
      )
    );
    this.userRestrictionsForm.markAsDirty();
    this.userRestrictionsForm.updateValueAndValidity();
  }

  formatSchedule(schedule: IUserRestrictionDay[] = []): string {
    const ordered = sortBy(schedule, (entry) =>
      DAYS_OF_WEEK.indexOf(entry.day)
    );
    const firstDay = first(ordered);
    if (!firstDay) {
      return 'No schedule set';
    }

    const sharedWindow = ordered.every(
      (entry) =>
        entry.allowedFrom === firstDay.allowedFrom &&
        entry.allowedTo === firstDay.allowedTo
    );

    if (sharedWindow) {
      const days = ordered.map((entry) => entry.day);
      return `${this._formatDayGroup(days)} ${this._formatTimeWindow(
        firstDay
      )}`;
    }

    return ordered
      .map(
        (entry) =>
          `${startCase(entry.day).slice(0, 3)} ${this._formatTimeWindow(entry)}`
      )
      .join(', ');
  }

  ipWhitelistTooltip(ipWhitelist?: IPracticeIpWhitelist): string {
    return `Only allowed from ${(ipWhitelist?.whitelist ?? []).join(', ')}`;
  }

  compareNamedDocuments(
    namedDocument: INamedDocument,
    selectedNamedDocument: INamedDocument
  ): boolean {
    return isSameRef(namedDocument, selectedNamedDocument);
  }

  async practiceSelected(practice: WithRef<IPractice>): Promise<void> {
    if (isSameRef(practice, this.selectedPractice())) {
      return;
    }

    const changePractice = (): void => {
      this.userRestrictions.set([]);
      this.ipExemptUsersCtrl.setValue([]);
      this.selectedPractice.set(practice);
      this.markFormAsPristine();
    };

    const saveDisabled = await snapshot(this.saveDisabled$);
    if (saveDisabled) {
      changePractice();
      return;
    }

    const confirmed = await this._basicDialog.confirm({
      title: 'Unsaved Changes',
      prompt: 'You have unsaved changes. Are you sure you want to continue?',
    });
    if (confirmed) {
      changePractice();
    }
  }

  async save(): Promise<void> {
    const practice = this.selectedPractice();
    if (!practice) {
      return;
    }

    const { isEnabled } = this.userRestrictionsForm.getRawValue();

    await Firestore.patchDoc(practice.ref, {
      restrictions: {
        ...practice.restrictions,
        ipWhitelist: this.ipWhitelistForm.getRawValue(),
        ipExemptUsers: this.ipExemptUsers(),
        userRestrictions: {
          isEnabled,
          restrictions: this.userRestrictions(),
        },
      },
    });

    this.markFormAsPristine();
    this._snackBar.open('Practice Restrictions Saved');
  }

  patchForm(restrictions?: IPracticeRestrictions): void {
    this.ipWhitelistForm.patchValue(
      restrictions?.ipWhitelist ?? {
        isEnabled: false,
        whitelist: [],
      }
    );
    this.userRestrictionsForm.patchValue({
      isEnabled: restrictions?.userRestrictions?.isEnabled,
    });
    this.ipExemptUsersCtrl.setValue(restrictions?.ipExemptUsers ?? []);
    this.scheduleGroup.resetToDefault();
    this.ipOverrideGroup.setIpWhitelist();
    this.userRestrictions.set(
      restrictions?.userRestrictions?.restrictions ?? []
    );
  }

  markFormAsPristine(): void {
    this.ipWhitelistForm.markAsPristine();
    this.ipWhitelistForm.updateValueAndValidity();
    this.userRestrictionsForm.markAsPristine();
    this.userRestrictionsForm.updateValueAndValidity();
    this.ipExemptUsersCtrl.markAsPristine();
    this.ipExemptUsersCtrl.updateValueAndValidity();
  }

  async toggleMultifactor(enabled: boolean): Promise<void> {
    const organisation = await snapshot(this.organisation.organisation$);
    if (!organisation) {
      return;
    }

    if (enabled) {
      await Firestore.patchDoc(organisation.ref, {
        settings: {
          ...(organisation.settings ?? {}),
          multifactor: { enabled: true },
        },
      });
      this._snackBar.open('Two-factor authentication enabled for workspace');
      return;
    }

    const users = await snapshot(Organisation.users$(organisation, false));
    const userRefs = users
      .filter(
        (user) => user.multifactorOverride !== MultifactorOverride.Required
      )
      .map(({ ref }) => ref);

    const confirmed = await this._basicDialog.confirm({
      title: 'Disable Two-Factor Authentication?',
      prompt: `This will remove all verification methods for ${userRefs.length} user(s). They will need to set up two-factor authentication again if re-enabled. This action cannot be undone.`,
    });
    if (!confirmed) {
      return;
    }

    try {
      const result = await this._multifactorAdmin.disableMultifactorForUsers(
        userRefs,
        organisation.ref
      );

      if (result.failedUids.length > 0) {
        this._snackBar.open(
          `Failed to disable two-factor authentication for ${result.failedUids.length} user(s). Workspace settings were not changed.`
        );
        return;
      }
    } catch {
      this._snackBar.open(
        'Could not disable two-factor authentication for workspace'
      );
      return;
    }

    await Firestore.patchDoc(organisation.ref, {
      settings: {
        ...(organisation.settings ?? {}),
        multifactor: { enabled: false },
      },
    });
    this._snackBar.open(
      `Two-factor authentication disabled for workspace (verification methods removed for ${userRefs.length} users)`
    );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  private _formatDayGroup(days: DayOfWeek[]): string {
    if (hasSameDays(days, DAYS_OF_WEEK)) {
      return 'All week';
    }
    if (hasSameDays(days, WEEK_DAYS)) {
      return 'Weekdays';
    }
    if (hasSameDays(days, WEEKEND_DAYS)) {
      return 'Weekends';
    }
    return DAYS_OF_WEEK.filter((day) => days.includes(day))
      .map((day) => startCase(day).slice(0, 3))
      .join(', ');
  }

  private _formatTimeWindow(day: IUserRestrictionDay): string {
    const from = formatTimeStringDisplay(day.allowedFrom);
    const to = formatTimeStringDisplay(day.allowedTo);
    return `${from} - ${to}`;
  }
}
